import Vue from 'vue';
import VueFormGenerator, {abstractField} from "vue-form-generator";
import Multiselect from 'vue-multiselect'
import validators from '@/providers/validation/validators.js';
import ButtonSelect from './ButtonSelect.vue';
import Boolean from './Boolean.vue';
import ColorPicker from './ColorPicker.vue';
import ImageSelect from './ImageSelect.vue';
import File from './File.vue';
import HtmlEditor from './HtmlEditor.vue';
import Datetime from './Datetime.vue';
import MonthSelect from './MonthSelect.vue';
import Tags from './Tags.vue';
import images from './images';
import _ from 'lodash'

import 'vue-multiselect/dist/vue-multiselect.min.css'

Vue.component("field-boolean", Boolean);
Vue.component("field-buttonselect", ButtonSelect);
Vue.component("field-imageselect", ImageSelect);
Vue.component("field-file", File);
Vue.component("field-color", ColorPicker);
Vue.component("field-datetime", Datetime);
Vue.component("field-month", MonthSelect);
Vue.component("field-html", HtmlEditor);
Vue.component("field-tags", Tags);
Vue.component("field-empty", {
  template: '<input type=hidden \
    v-model="value" \
    :name="schema.inputName" \
    :id="getFieldID(schema)" \
    :class="schema.fieldClasses" \
    />',
  mixins: [abstractField],
});
Vue.component("multiselect", Multiselect);

//API escapes html characters. Unescape them for ordinary fields.
abstractField.methods.formatValueToField = function (value) {
  if (
    this.schema.type &&
    ['input', 'textarea'].includes(String(this.schema.type).toLowerCase()) &&
    typeof value === 'string'
  ) {
    return _.unescape(value);
  }
  return value;
};

//Vue form generator fix. Compare fields by their name and model instead of schema object
VueFormGenerator.component.methods.onFieldValidated = function (res, errors, field) {
  // Remove old errors for this field
  this.errors = this.errors.filter(e => {
    return e.field.name !== field.schema.name || e.field.model !== field.schema.model;
  });
  if (!res && errors && errors.length > 0) {
    // Add errors with this field
    _.forEach(errors, (err) => {
      this.errors.push({
        field: field.schema,
        error: err
      });
    });
  }

  let isValid = this.errors.length === 0;
  this.$emit("validated", isValid, this.errors);
};
VueFormGenerator.component.methods.onModelUpdated = function (newVal, schema) {
  this.$emit("model-updated", newVal, schema);
  this.validate();
}
var watcher = {
  deep: true,
  handler () {
    this.$nextTick(this.validate);
  },
};
VueFormGenerator.component.watch.fields = VueFormGenerator.component.watch.groups = watcher;

Vue.use(VueFormGenerator);

var addValidators = field => {
  //email
  var checkRequired = true;
  if (field.inputType) {
    for (let type of ['email', 'number', 'url']) {
      if (field.inputType === type && !field.validator.includes(validators[type])) {
        field.validator.push(validators[type]);
        checkRequired = false;
      }
    }
  }
  if (field.hasOwnProperty('minlen') || field.hasOwnProperty('maxlen')) {
    if (field.hasOwnProperty('minlen') && !field.hasOwnProperty('min')) {
      field.min = field.minlen;
    }
    if (field.hasOwnProperty('maxlen') && !field.hasOwnProperty('max')) {
      field.max = field.maxlen;
    }
    checkRequired = false;
    field.validator.push(validators['string']);
  }
  if (field.type === 'tags' && field.required) {
    field.validator.push(validators['array']);
  }
  if (checkRequired && field.required && !field.validator.includes(validators.required)) {
    field.validator.push(validators.required);
  }
  return field;
};

const form = {
  //must have 'protofields' in data
  //use <VueForm v-if="formReady" :options=formOptions :schema=schema :model=item @validated=validated />
  mixins: [images],
  components:{
    VueForm: VueFormGenerator.component
  },
  data () {return {
    dirt: 'item',
    dirtpile: {},
    singlechange: 0,
    isValid: undefined,
    formOptions: {
      validateAfterLoad: true,
      validateAfterChanged: true,
    },
  }},
  computed: {
    dirty () {
      if (this.dirt) {
        return this.singlechange > 0;
      } else {
        return true;
      }
    },
    formReady () {
      return this.schema && Object.keys(this.schema).length;
    },
    imageKeys () {
      return this.fldTypeKeys('imageselect');
    },
    tagsKeys () {
      return this.fldTypeKeys('tags');
    },
    flatKeys () {
      return this.fldTypeKeys(false);
    },
    nonImageValues () {
      if (this.imageKeys || this.tagsKeys) {
        return _.omit(this.item, ['images'].concat(this.imageKeys || [], this.tagsKeys || []));
      } else {
        return this.item;
      }
    },
  },
  asyncComputed: {
    schema: {
      get () {
        if (!this.protofields) {
          return null;
        }
        var defaults = Object.assign({
          type: 'input',
          inputType: 'text',
          label: null,
          required: true,
          validator: [],
        }, this.defaults || {});
        var fields = [];
        var setFields = f => ({
            fields: this.$fields
              .expand(f.hasOwnProperty('legend') && f.fields || f, defaults)
              .map(addValidators)
            ,
            legend: f.legend || '',
          })
        ;
        if (Array.isArray(this.protofields)) {
          fields = this.protofields.map(setFields);
        } else if (this.protofields) {
          fields = [setFields(this.protofields)];
        }
        var resolving = fld => {
          //if field has async values, return the fields after fetching values
          if (fld.hasOwnProperty('values') && typeof fld.values === 'function') {
            let result = fld.values();
            if (Promise.resolve(result) === result) {  //this is a way of checking if something is a promise
              return result
                .then(values => this.$fields.setValues(fld, values))
              ;
            }
          }
          //else return field
          return new Promise(r => r(fld));
        };

        //turn fields to promises (to resolve any async properties)
        if (Array.isArray(fields)) {
          return Promise.all(fields.map(group =>
              Promise.all((group.hasOwnProperty('legend') && group.fields || group).map(resolving)) //passes all fields in group
                .then(groupfields => ({ //returns an object for each group, containing fields and legend
                  fields: groupfields,
                  legend: group.legend || '',
                }))
              ))
              .then(groups => ({groups}))
          ;
        } else {
          return Promise.all(fields.fields.map(resolving))
            .then(fields => ({fields}))
          ;
        }
      },
      default: {},
      watch () {
        this.protofields;
      }
    }
  },
  methods: {
    fldTypeKeys (fldType) {
      if (!this.schema || (!this.schema.fields && !this.schema.groups)) {
        return;
      }
      var fltfn = fld => fld.type === fldType || !fldType;
      var filter = f => Object.values(f)
        .filter(fltfn)
        .map(f => f.model)
      ;
      if (this.schema.groups) {
        return this.schema.groups
          .map(g => filter(g.fields))
          .reduce((acc, current) => acc.concat(current), [])
        ;
      }
      if (this.schema.fields) {
        return filter(this.schema.fields);
      }
      return Object.values(this.schema.fields)
        .filter(fltfn)
        .map(f => f.model)
      ;
    },
    noteDirt () {
      if (this.dirt && typeof this[this.dirt] === 'object') {
        this.dirtpile = _.clone(this[this.dirt]);
      }
    },
    clean () {
      this.$nextTick(() => {
        this.singlechange = 0;
      });
    },
    validated (isValid, errors) {
      this.isValid = isValid;
      this.$emit('validated', isValid);
    },
  },
  mounted () {
    if (this.dirt) {
      this.noteDirt();
      if (typeof this[this.dirt] !== 'object') {
        this.singlechange = 1;
        return;
      }
      this.$watch(String(this.dirt), {
        deep: true,
        handler (val) {
          var prev = this.dirtpile;
          //both are valid and keys match (model keys not changed)
          if (
            this.flatKeys &&
            typeof val === 'object' &&
            typeof prev === 'object'&&
            val &&
            prev &&
            _.isEqual(Object.keys(val).sort(), Object.keys(prev).sort())
          ) {
            var changes = 0;
            for (let key of this.flatKeys) {
              if (!_.isEqual(val[key], prev[key])) {
                changes++;
              }
            }
            if (changes === 1) {
              this.singlechange++;
            } else if (changes > 1) {
              this.clean();
            }
          }
          return this.noteDirt();
        },
      })
    }
  },
}

export default form;


// WEBPACK FOOTER //
// ./src/helpers/mixins/form/index.js